import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AuthProviderResponse, DevLoginResponse, DevPersona, DevPersonasResponse } from '../../shared/api-types.js';
import { SIGNED_OUT_REASON_LABELS, SUBJECT_STATE_WORDS, isOneOf, SIGNED_OUT_REASONS, type AuthMode, type SignedOutReason } from '../../shared/enums.js';
import { safeReturnPath } from '../../shared/text.js';
import { api, signInUrl, type ApiFailure } from '../api.js';
import { SIGN_IN_EXECUTIVE_SENTENCE } from '../pages/lib/executive-disclosure.js';
import { Avatar } from '../ui/Avatar.js';
import { Banner } from '../ui/Banner.js';
import { Button, LinkButton } from '../ui/Button.js';
import type { Crumb } from '../ui/Breadcrumb.js';
import { Chip } from '../ui/Chip.js';
import { PAGE_TITLE_ID, Page, PageHead } from '../ui/PageHead.js';
import { FailureView, StateView, inlineFailureText } from '../ui/StateView.js';
import { StatusPill, type Tone } from '../ui/StatusPill.js';
import { useRequest } from '../ui/use-request.js';
import { AppMark } from './AppMark.js';

// The shell-owned pages: the dead ends and the signed-out surfaces (app-baseline 05-client §5.3).

/** A URL that matches nothing says so and offers a way back — never a silent redirect home. */
export function NotFoundPage({ strip }: { strip?: ReactNode }) {
  return (
    <Page measure="reading">
      <PageHead title="Page not found" strip={strip} />
      <StateView
        kind="not-found"
        title="We couldn't find that page"
        description="The link may be out of date, or the address may have been mistyped."
        action={<LinkButton to="/" variant="primary">Go to overview</LinkButton>}
        minHeight="40dvh"
      />
    </Page>
  );
}

export interface PermissionDeniedPageProps {
  /** What was refused, in the user's words: "this administration section". */
  what?: string;
  strip?: ReactNode;
  crumbs?: readonly Crumb[];
  title?: string;
}

/** Distinct from not-found and from empty: access is restricted, and here is how to request it. */
export function PermissionDeniedPage({ what = 'this page', strip, crumbs, title = 'Access restricted' }: PermissionDeniedPageProps) {
  return (
    <Page measure="reading">
      <PageHead title={title} strip={strip} crumbs={crumbs} />
      <StateView
        kind="denied"
        title={`You do not have access to ${what}`}
        description="Ask an administrator to add you to a group that grants it."
        action={<LinkButton to="/" variant="primary">Go to overview</LinkButton>}
        minHeight="40dvh"
      />
    </Page>
  );
}

// Why "Pending approval": an admission awaiting an administrator is 05-content §6's Pending, qualified by what it
// awaits — the roster's own word for the account (SUBJECT_STATUS_LABELS); "Waiting" sits on Pending's Not-list.
const SIGNED_OUT_TITLES: Record<SignedOutReason, string> = {
  signed_out: "You're signed out",
  not_allowed: "This account can't sign in",
  pending: 'Pending approval',
  expired: 'Sign-in timed out',
  error: "Sign-in didn't complete",
};

/** /signed-out?reason=signed_out|not_allowed|pending|expired|error */
export function SignedOutPage() {
  const [params] = useSearchParams();
  const raw = params.get('reason');
  const reason: SignedOutReason = isOneOf(SIGNED_OUT_REASONS, raw) ? raw : 'signed_out';
  return (
    <Page measure="reading">
      <PageHead title={SIGNED_OUT_TITLES[reason]} documentTitle={`${SIGNED_OUT_TITLES[reason]} · Calis`} />
      <div className="sheet stack">
        {/* Why: a plain sign-out's label only restates the h1; the other reasons' labels add the why. */}
        {reason !== 'signed_out' ? <p className="text-md">{SIGNED_OUT_REASON_LABELS[reason]}</p> : null}
        <div className="cluster">
          <LinkButton href={signInUrl('/')} variant="primary">{reason === 'signed_out' ? 'Sign in' : 'Sign in again'}</LinkButton>
        </div>
      </div>
    </Page>
  );
}

/**
 * The signed-out landing. MeProvider renders it in place when /api/me answers 401, at whichever
 * URL was visited — it has no route of its own, so the address bar keeps the page the person
 * wanted and the button's returnTo is that page. One h1, one sentence, one button.
 *
 * Why the button asks GET /auth/provider for its label: the signed-out client does not know
 * AUTH_MODE, and "Sign in with Google" is honest only where Google is what follows. Until the
 * answer arrives — and if it never does — the label is "Sign in", right in every mode because
 * /auth/login decides; the page never depends on the endpoint to work.
 *
 * The same answer says whether executive intelligence is on (ADR 0019; the owner's pick E13, design §2.14 item 5): while
 * it is, the page tells a member before they sign in that every member's summaries go into the executives' daily report,
 * and sends them to Settings → Your data for the rest. Read as off until the answer arrives, and if it fails — the
 * sentence is said only where the server says the floor is met.
 */
export function SignInPage({ returnTo }: { returnTo: string }) {
  const provider = useRequest<AuthProviderResponse>((signal) => api.get<AuthProviderResponse>('/auth/provider', { signal, noRedirect: true }), []);
  const mode: AuthMode | null = provider.status === 'ready' ? provider.data?.provider ?? null : null;
  const executiveOn = provider.status === 'ready' && provider.data?.executive === 'on';
  useEffect(() => {
    document.title = 'Sign in · Calis';
  }, []);
  return (
    <Page measure="reading" className="sign-in">
      <header className="sign-in__head">
        {/* Decorative: the heading carries the name (01-foundations §7.3). */}
        <AppMark className="sign-in__mark" decorative />
        <h1 id={PAGE_TITLE_ID} tabIndex={-1} className="sign-in__title">Sign in to Calis</h1>
        <p className="sign-in__lead">Calis imports your Fellow meetings, summarises each one, and turns what was agreed into your <span className="nowrap">to-do</span> list.</p>
      </header>
      <div className="sheet sign-in__sheet">
        <LinkButton href={signInUrl(returnTo)} variant="primary" size="lg" block>
          {mode === 'google' ? 'Sign in with Google' : 'Sign in'}
        </LinkButton>
        {mode === 'dev' ? <p className="sign-in__note secondary text-sm">Development sign-in — you choose a persona on the next page.</p> : null}
        <p className="sign-in__note secondary text-sm">Calis is for Centro staff. Access is granted by an administrator — sign in to request it.</p>
        {executiveOn ? <p className="sign-in__note secondary text-sm" data-executive-notice="">{SIGN_IN_EXECUTIVE_SENTENCE}</p> : null}
      </div>
    </Page>
  );
}

// Why these tones: the same four the roster's state pill uses, so a persona reads the same here as there.
const STATUS_TONE: Record<DevPersona['status'], Tone> = { approved: 'success', pending: 'warning', denied: 'danger', suspended: 'neutral' };

/**
 * /dev-sign-in — lists GET /auth/dev/personas and signs in as one through POST /auth/dev/login.
 * Only available when the server runs with AUTH_MODE=dev; production refuses that at boot.
 */
export function DevSignInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = safeReturnPath(params.get('returnTo')) ?? '/';
  const personas = useRequest<DevPersonasResponse>((signal) => api.get<DevPersonasResponse>('/auth/dev/personas', { signal, noRedirect: true }), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const signInAs = async (persona: DevPersona): Promise<void> => {
    setBusyId(persona.id);
    setFailure(null);
    const result = await api.post<DevLoginResponse>('/auth/dev/login', { subjectId: persona.id, returnTo }, { noRedirect: true });
    if (result.ok) {
      // Why: a full navigation, so the authenticated shell loads /api/me fresh with the new cookie.
      window.location.assign(safeReturnPath(result.data.redirectTo) ?? '/');
      return;
    }
    setBusyId(null);
    if (result.code === 'sign_in_pending') {
      navigate('/signed-out?reason=pending');
      return;
    }
    setFailure(result);
  };

  return (
    <Page measure="reading">
      <PageHead title="Development sign-in" />
      <Banner tone="warning" title="Development sign-in — not available in production" role="none">
        These personas exist only for demos and tests.
      </Banner>
      {failure ? (
        <Banner tone="danger" title="Sign-in failed" onDismiss={() => setFailure(null)}>
          <p>{inlineFailureText(failure)}</p>
          {failure.correlationId ? <p className="text-xs muted">Reference <code className="selectable">{failure.correlationId}</code></p> : null}
        </Banner>
      ) : null}
      {personas.status === 'loading' ? <StateView kind="loading" label="Loading personas" minHeight="12rem" /> : null}
      {personas.status === 'error' && personas.error ? (
        personas.error.status === 404 ? (
          <StateView
            kind="not-configured"
            title="Development sign-in is not switched on here"
            description="This installation uses Google Workspace sign-in."
            action={<LinkButton href={signInUrl('/')} variant="primary">Sign in with Google</LinkButton>}
          />
        ) : (
          <FailureView failure={personas.error} what="the personas" onRetry={personas.reload} />
        )
      ) : null}
      {personas.status === 'ready' && personas.data ? (
        personas.data.personas.length === 0 ? (
          <StateView kind="empty" title="No personas have been seeded" description={<>Run <code>npm run demo</code>, then reload this page.</>} />
        ) : (
          <ul className="persona-list" role="list">
            {personas.data.personas.map((persona) => (
              <li key={persona.id} className="persona card">
                <Avatar name={persona.displayName} email={persona.email} hashKey={persona.id} initials={persona.initials} size="lg" decorative />
                <div className="persona__who">
                  <p className="persona__name">{persona.displayName}</p>
                  <p className="persona__email">{persona.email}</p>
                  <div className="cluster">
                    <StatusPill tone={STATUS_TONE[persona.status]}>{SUBJECT_STATE_WORDS[persona.status]}</StatusPill>
                    {persona.isSuperuser ? <Chip tone="accent" icon="shield">Superuser</Chip> : null}
                  </div>
                </div>
                <Button variant="secondary" onClick={() => void signInAs(persona)} loading={busyId === persona.id} disabled={busyId !== null && busyId !== persona.id}>
                  Sign in as {persona.displayName.split(' ')[0]}
                </Button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Page>
  );
}
